/**!
 * Created by Leonardo Cheslacki on 02/09/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageVideo extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        this.state = {};

        this.$current_frame = 1;

        this.$element_video = null;
        this.$element_source = null;

        _this = this;
    };

    $functions = {
        handleBack: function (event, object) {
            _this.$current_frame = 1;

            let occ_text = _this.verifyField(object, '_this_sub_frame.occ_text');
           
            occ_text && occ_text.gotoAndStop('empty');

            _this.getFrame().gotoAndPlay('continue');

            _this.close();
        },

        /**
         *
         * @param event
         * @param _this_frame
         */
        handleBackFrame: function (event, {_this_frame}) {
            _this.gotoAndStopFrame(_this_frame, 1);

            _this.close();
        },

        /**
         *
         * @param event
         * @param frame
         */
        handleNavigate: function (event, {frame}) {
            _this.$current_frame = frame;

            _this.getFrame().gotoAndPlay('continue');
        },

        /**
         *
         * @param event
         * @param _this_frame
         * @param frame
         */
        handleSubNavigate: function (event, {_this_frame, frame}) {
            const
                {content_video} = _this.props,
                occ = _this.padWithLeadingZeros(frame, 2),
                _this_sub_frame = _this_frame[`occ_frame_${occ}`],
                {btn_frame01} = _this_sub_frame;

            _this.gotoAndStopFrame(_this_frame, frame);

            let uri = _this.verifyField(content_video, `frame_${_this.padWithLeadingZeros(_this.$current_frame, 2)}.${occ}`);

            if (uri) {
                const {btn_play} = _this_sub_frame;

                _this.appendVideo(btn_play, 'sub-navigate', {
                    uri: uri,
                    style: 'bottom: initial;'
                });
            }

            _this._handleBackFrame && btn_frame01.off('mousedown', _this._handleBackFrame);
            _this._handleBackFrame = btn_frame01.on('mousedown', _this.$functions.handleBackFrame, false, false, {
                _this_frame: _this_frame
            });
        },

        /**
         *
         * @param event
         * @param options
         */
        handleReadVideo: function (event, options) {
            let
                uri = _this.verifyField(options, 'uri'),
                autoplay = _this.verifyField(options, 'autoplay'),
                style = _this.verifyField(options, 'style');

            if (uri) {
                if (!_this.isElement(_this.$element_video, 'video')) {
                    const anim_container = document.getElementById('animation_container');
                    _this.$element_source = document.createElement('SOURCE');
                    _this.$element_video = document.createElement('VIDEO');
                    _this.$element_video.appendChild(_this.$element_source);
                    anim_container.appendChild(_this.$element_video);
                }

                _this.setAttributes(_this.$element_video, {
                    id: 'dom_overlay_container',
                    class: 'animate__animated animate__fadeIn'
                });

                _this.setAttributes(_this.$element_video, {
                    style: 'display: block;'
                });

                if (style) {
                    _this.setAttributes(_this.$element_video, {style: `${style} display: block;`});
                } else {
                    _this.setAttributes(_this.$element_video, {style: 'display: block;'});
                }

                _this.setAttributes(_this.$element_source, {
                    src: uri,
                    id: 'source_overlay_container',
                    type: 'video/mp4'
                });

                _this.$element_video.load();

                const play = () => {
                    _this.setAttributes(_this.$element_video, {
                        width: _this.calcSize(1, stage.scaleX, _this.$element_video.videoWidth, false, 'int'),
                        height: _this.calcSize(1, stage.scaleY, _this.$element_video.videoHeight, false, 'int')
                    });

                    _this.$element_video.play();
                };

                const animateEnd = ({animationName}) => {
                    (animationName === 'fadeOut') && _this.removeListener('animationend', 'animationend-video', _this.destroyVideo);
                };

                _this.removeListener('loadedmetadata', 'load-video', () => {
                    _this.addListener(_this.$element_video, 'loadedmetadata', 'load-video', play);
                });

                _this.removeListener('ended', 'ended-video', () => {
                    _this.addListener(_this.$element_video, 'ended', 'ended-video', _this.close);
                });

                _this.removeListener('animationend', 'animationend-video', () => {
                    _this.addListener(_this.$element_video, 'animationend', 'animationend-video', animateEnd);
                });

                if (autoplay) {
                    _this.setAttributes(_this.$element_video, {loop: true});
                    _this.$element_video.play();
                } else {
                    _this.setAttributes(_this.$element_video, {loop: false});
                }
            }
        }
    };

    close = () => {
        _this.setAttributes(_this.$element_video, {class: 'animate__animated animate__fadeOut'});
    };

    destroyVideo = () => {
        if (this.isElement(this.$element_video, 'video')) {
            this.$element_video.pause();
            _this.setAttributes(_this.$element_video, {style: 'display: none;'});
        }
    };

    /**
     *
     * @param occurrence
     * @param name
     * @param options
     */
    appendVideo = (occurrence, name, options = {}) => {
        this.close();

        if (this.verifyField(options, 'autoplay')) {
            this.$functions.handleReadVideo(null, options);
        } else {
            this.removeListener('mousedown', `mousedown-open-${name}`, () => {
                this.addListener(occurrence, 'mousedown', `mousedown-open-${name}`, this.$functions.handleReadVideo, false, options);
            });
        }
    };

    startVideo = () => {
        this.main.on('animate_end', ({current_name}) => {
            const occ = this.padWithLeadingZeros(this.$current_frame, 2);

            this.gotoAndStopFrame(this.get(), this.$current_frame);

            this.getFrame().gotoAndPlay('start');

            if (this.inArray(['02', '03', '04', '05'], occ)) {
                const {occ_video} = this.getFrame();

                this.gotoAndStopFrame(occ_video, 1);

                const
                    {occ_video: {currentLabel}} = this.getFrame(),
                    _this_sub_frame = occ_video[`occ_frame_${this.padWithLeadingZeros(this.toNumber(currentLabel), 2)}`],
                    {btn_back} = _this_sub_frame;

                const execute = (frame) => {
                    let occ_sub = this.padWithLeadingZeros(frame, 2);

                    this[`_handleSubNavigate${occ}${occ_sub}`] && _this_sub_frame[`btn_frame${occ_sub}`].off('mousedown', this[`_handleSubNavigate${occ}${occ_sub}`]);
                    this[`_handleSubNavigate${occ}${occ_sub}`] = _this_sub_frame[`btn_frame${occ_sub}`].on('mousedown', this.$functions.handleSubNavigate, false, false, {
                        _this_frame: occ_video,
                        frame: frame
                    });
                };

                if (this.inArray(['02', '03', '05'], occ)) {
                    const
                        {content_video, default_video} = this.props,
                        {btn_play} = _this_sub_frame;

                    let options = {
                        uri: this.verifyField(content_video, `frame_${occ}.${default_video}`),
                        autoplay: this.inArray(['03', '05'], occ),
                        style: (occ !== '05') ? 'bottom: initial;' : 'z-index: -1;'
                    };

                    if (occ === '05') {
                        for (let i = 1; i < 8; i++) {
                            let occ_joy = this.padWithLeadingZeros(i, 2);
                            this[`_handleJoy${occ}${occ_joy}`] && _this_sub_frame[`btn_joy${occ_joy}`].off('mousedown', this[`_handleJoy${occ}${occ_joy}`]);
                            this[`_handleJoy${occ}${occ_joy}`] = _this_sub_frame[`btn_joy${occ_joy}`].on('mousedown', () => {
                                _this_sub_frame.occ_text.gotoAndPlay(`joy${occ_joy}`);
                            });
                        }
                    }

                    this.appendVideo(btn_play, 'navigate', options);
                }

                if (occ === '02') {
                    for (let i = 2; i < 6; i++) {
                        execute(i);
                    }
                }

                if (occ === '04') {
                    for (let i = 2; i < 7; i++) {
                        execute(i);
                    }
                }

                this._handleBack && btn_back.off('mousedown', this._handleBack);
                this._handleBack = btn_back.on('mousedown', this.$functions.handleBack, false, false, {
                    _this_sub_frame: _this_sub_frame
                });
            }
        });
    };

    /**
     *
     */
    initVideo = () => {
        const
            _this_current = this.get(),
            {totalFrames} = _this_current;

        let
            diff = 2,
            length = (totalFrames - diff);

        for (let i = 0; i < length; i++) {
            let
                frame = (i + diff),
                occ = this.padWithLeadingZeros(frame, 2);

            this[`_handleNavigate${occ}`] && _this_current[`btn_frame${occ}`].off('mousedown', this[`_handleNavigate${occ}`]);
            this[`_handleNavigate${occ}`] = _this_current[`btn_frame${occ}`].on('mousedown', this.$functions.handleNavigate, false, false, {
                frame: frame
            });

            this.endFor(length, i) && this.startVideo();
        }
    };
}

/**
 *
 * @type {{}}
 */
StageVideo.propTypes = {
    default_video: PropTypes.string,
    content_video: PropTypes.object
};

/**
 *
 * @type {{}}
 */
StageVideo.defaultProps = {
    default_video: '01',
    content_video: {
        frame_02: {
            '01': '/public/media/02/1_EasyBoating.mp4',
            '02': '/public/media/02/5_Joystick.mp4',
            '03': '/public/media/02/4_AncoragemVirtual.mp4',
            '04': '/public/media/02/3_GlassCockpit.mp4',
            '05': '/public/media/02/2_AntiCorrosao.mp4'
        },
        frame_03: {
            '01': '/public/media/04/Tela_Motor_IPS.mp4'
        },
        frame_05: {
            '01': '/public/media/05/Video_redondo_Tela_4_V2.mp4'
        }
    }
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageVideo, {className: 'StageVideo'});
